import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, Star, WalletCards } from "lucide-react";
import { API_BASE_URL as API } from "../apiConfig";
import RetailMemberAuthPanel from "../components/retail/RetailMemberAuthPanel";
import PublicBrandMark from "../components/public/PublicBrandMark";

const ratingFields = [
  ["overall_experience", "Overall Experience"],
  ["vehicle_cleanliness_comfort", "Vehicle Cleanliness & Comfort"],
  ["driver_service", "Driver Service"],
  ["customer_support", "Customer Support"],
  ["punctuality", "Punctuality"],
];

function StarRating({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((rating) => (
        <button key={rating} type="button" onClick={() => onChange(rating)} className="rounded-lg p-1">
          <Star className={`h-7 w-7 ${rating <= value ? "fill-[#d8b769] text-[#d8b769]" : "text-white/20"}`} />
        </button>
      ))}
    </div>
  );
}

export default function PublicReviewPage() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") || "", []);
  const [review, setReview] = useState(null);
  const [ratings, setRatings] = useState({
    overall_experience: 5,
    vehicle_cleanliness_comfort: 5,
    driver_service: 5,
    customer_support: 5,
    punctuality: 5,
  });
  const [additionalComments, setAdditionalComments] = useState("");
  const [nameVerificationAnswer, setNameVerificationAnswer] = useState("");
  const [retailMember, setRetailMember] = useState(null);
  const [retailMemberToken, setRetailMemberToken] = useState(localStorage.getItem("retail_member_token") || "");
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Review link is missing.");
      return;
    }
    axios.get(`${API}/public-booking/reviews/by-token`, { params: { token } })
      .then((response) => {
        setReview(response.data.review);
        setStatus("ready");
      })
      .catch((error) => {
        setStatus("error");
        setMessage(error.response?.data?.detail || "Review link is invalid or expired.");
      });
  }, [token]);

  useEffect(() => {
    if (!retailMemberToken) return;
    axios.get(`${API}/public-booking/member/me`, { headers: { "X-Retail-Member-Token": retailMemberToken } })
      .then((response) => setRetailMember(response.data.member))
      .catch(() => {});
  }, [retailMemberToken]);

  const submitReview = async () => {
    setLoading(true);
    setMessage("");
    try {
      const response = await axios.post(
        `${API}/public-booking/reviews/submit`,
        {
          token,
          ...ratings,
          additional_comments: additionalComments,
          name_verification_answer: nameVerificationAnswer,
        },
        retailMemberToken ? { headers: { "X-Retail-Member-Token": retailMemberToken } } : undefined,
      );
      if (response.data.requires_member_login) {
        setRequiresLogin(true);
        setMessage("Review submitted. Please log in or register to claim your JPY 2,000 credit.");
      } else {
        setStatus("claimed");
        setMessage("Thank you. JPY 2,000 credit has been added to your member account.");
      }
    } catch (error) {
      setMessage(error.response?.data?.detail || "We could not submit this review.");
    } finally {
      setLoading(false);
    }
  };

  const claimCredit = async (member, memberToken) => {
    const activeToken = memberToken || retailMemberToken;
    setRetailMember(member);
    setRetailMemberToken(activeToken);
    if (!activeToken) return;
    try {
      await axios.post(`${API}/public-booking/reviews/claim`, { token }, { headers: { "X-Retail-Member-Token": activeToken } });
      setStatus("claimed");
      setMessage("JPY 2,000 credit has been added to your member account.");
    } catch (error) {
      setMessage(error.response?.data?.detail || "Please sign in to claim your credit.");
    }
  };

  return (
    <main className="min-h-screen bg-[#030303] px-4 py-6 text-white">
      <section className="mx-auto max-w-3xl overflow-hidden rounded-[34px] border border-[#d8b769]/25 bg-[#0d0c0a] shadow-2xl shadow-black">
        <div className="border-b border-white/10 bg-black px-5 py-5">
          <div className="flex items-center gap-3">
            <div>
              <PublicBrandMark href="/book" />
              <h1 className="mt-1 text-2xl font-semibold">Service Review</h1>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-white/60">Thank you for choosing our private transfer service. Your feedback helps us keep every ride precise, clean, and comfortable.</p>
        </div>

        {status === "loading" && <div className="p-6 text-white/60">Loading review details...</div>}
        {status === "error" && <div className="p-6 text-[#f3d98b]">{message}</div>}
        {status !== "loading" && status !== "error" && (
          <div className="grid gap-5 p-5">
            <div className="rounded-[26px] border border-white/10 bg-white/[0.04] p-5">
              <div className="grid gap-3 text-sm text-white/70 sm:grid-cols-2">
                <p><span className="text-white/40">Booking:</span> {review?.confirmation_code || review?.order_no}</p>
                <p><span className="text-white/40">Service date:</span> {review?.service_date} {review?.pickup_time}</p>
                <p><span className="text-white/40">Route:</span> {review?.route}</p>
                <p><span className="text-white/40">Vehicle:</span> {review?.vehicle_type}</p>
                <p><span className="text-white/40">Driver:</span> {review?.driver_label}</p>
              </div>
            </div>

            {status === "claimed" ? (
              <div className="rounded-[26px] border border-[#d8b769]/30 bg-[#d8b769]/10 p-6 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-[#d8b769]" />
                <p className="mt-3 text-lg font-semibold">Credit claimed</p>
                <p className="mt-2 text-sm text-white/60">{message}</p>
              </div>
            ) : (
              <>
                <div className="rounded-[26px] border border-white/10 bg-black/30 p-5">
                  <div className="grid gap-5">
                    {ratingFields.map(([field, label]) => (
                      <div key={field} className="flex flex-col gap-2 border-b border-white/10 pb-4 last:border-b-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-sm font-semibold">{label}</span>
                        <StarRating value={ratings[field]} onChange={(value) => setRatings((prev) => ({ ...prev, [field]: value }))} />
                      </div>
                    ))}
                  </div>
                  <label className="mt-5 grid gap-2 text-sm font-semibold">
                    Additional comments
                    <textarea
                      className="min-h-[110px] rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm text-black outline-none"
                      value={additionalComments}
                      onChange={(event) => setAdditionalComments(event.target.value)}
                      placeholder="Optional"
                    />
                  </label>
                  <label className="mt-5 grid gap-2 text-sm font-semibold">
                    Name verification: {review?.name_verification_prompt}
                    <input
                      className="rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm text-black outline-none"
                      value={nameVerificationAnswer}
                      onChange={(event) => setNameVerificationAnswer(event.target.value)}
                      placeholder="Enter the missing letter(s)"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={submitReview}
                    className="mt-5 inline-flex w-full items-center justify-center rounded-2xl bg-[#d8b769] px-5 py-4 text-sm font-bold text-black disabled:opacity-50"
                  >
                    {loading ? "Submitting..." : "Submit review"}
                  </button>
                </div>

                {(requiresLogin || !retailMember) && (
                  <RetailMemberAuthPanel onMemberChange={claimCredit} />
                )}

                {retailMember && requiresLogin && (
                  <button type="button" onClick={() => claimCredit(retailMember, retailMemberToken)} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#d8b769]/40 px-5 py-3 text-sm font-semibold text-[#f3d98b]">
                    <WalletCards className="h-4 w-4" />
                    Claim JPY 2,000 credit
                  </button>
                )}
              </>
            )}

            {message && status !== "claimed" && <p className="text-sm text-[#f3d98b]">{message}</p>}
          </div>
        )}
      </section>
    </main>
  );
}
